import { useState, useEffect, useCallback } from 'react';
import { userPreferencesService } from '../services/userPreferencesService';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook para gestionar columnas dinámicas
 * @param {string} tableId - Identificador único de la tabla (ej: 'T1')
 * @param {Array} allColumnsUniverse - Todas las columnas posibles [{id: 'vin', label: 'VIN'}, ...]
 * @param {Array} defaultColumnIds - IDs de las columnas por defecto ['vin', 'marca']
 */
export const useTableColumns = (tableId, allColumnsUniverse, defaultColumnIds) => {
  const auth = useAuth();

  // 🛡️ CORRECCIÓN AQUÍ: 
  // Obtenemos el ID de forma segura. Si auth o auth.user no existen, usamos el ID 1 por defecto.
  const userId = auth?.user?.id || 1;

  const [visibleColumns, setVisibleColumns] = useState(() => {
    return defaultColumnIds
      .map(colId => allColumnsUniverse.find(c => c.id === colId))
      .filter(Boolean);
  });
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);

  // Cargar configuración inicial
  useEffect(() => {
    let mounted = true;

    const loadConfig = async () => {
      setIsLoadingConfig(true);
      try {
        // Usamos userId seguro aquí
        const savedOrder = await userPreferencesService.getTableConfig(userId, tableId);

        if (savedOrder && mounted) {
          // 1. Filtramos del universo solo las que están guardadas
          const orderedColumns = savedOrder
            .map(colId => allColumnsUniverse.find(c => c.id === colId))
            .filter(Boolean); // Eliminar undefined si alguna columna dejó de existir

          // Si por alguna razón la config guardada está vacía o corrupta, usamos defaults
          if (orderedColumns.length > 0) {
            setVisibleColumns(orderedColumns);
          } else {
            const defaults = defaultColumnIds
              .map(colId => allColumnsUniverse.find(c => c.id === colId))
              .filter(Boolean);
            setVisibleColumns(defaults);
          }
        } else if (mounted) {
          // Si no hay config guardada, usar default
          const defaults = defaultColumnIds
            .map(colId => allColumnsUniverse.find(c => c.id === colId))
            .filter(Boolean);

          setVisibleColumns(defaults);
        }
      } catch (err) {
        console.error("Error loading columns", err);
        if (mounted) {
          const defaults = defaultColumnIds
            .map(colId => allColumnsUniverse.find(c => c.id === colId))
            .filter(Boolean);
          setVisibleColumns(defaults);
        }
      } finally {
        if (mounted) setIsLoadingConfig(false);
      }
    };

    loadConfig();

    return () => { mounted = false; };
    // Usamos userId como dependencia, no user.id
  }, [userId, tableId, JSON.stringify(defaultColumnIds)]);

  // Guardar nueva configuración
  const saveColumnOrder = useCallback(async (newColumnObjects) => {
    // Optimistic UI update
    setVisibleColumns(newColumnObjects);

    const idsToSave = newColumnObjects.map(c => c.id);
    // Usamos userId seguro aquí también
    await userPreferencesService.saveTableConfig(userId, tableId, idsToSave);
  }, [userId, tableId]);

  return {
    visibleColumns,
    isLoadingConfig,
    saveColumnOrder
  };
};